import React, { useState, useEffect, useCallback } from 'react';

import { Select } from 'antd';

import { adsRulesApi } from '../../../../shared/common/api/endpoints/adsRulesApi';
import { CampaignItem } from '../../types';

interface AdsMultiSelectProps {
  value: string[];
  onChange: (ids: string[]) => void;
  options?: { id: string; name: string }[];
  disabled?: boolean;
  loading?: boolean;
  sourceId?: number;
  accountId?: string;
  campaignIds?: string[];
  groupIds?: string[];
  search?: string;
}

export function AdsMultiSelect({
  value,
  onChange,
  options,
  disabled = false,
  loading: externalLoading,
  sourceId,
  accountId,
  campaignIds,
  groupIds,
  search
}: AdsMultiSelectProps) {
  const [apiOptions, setApiOptions] = useState<CampaignItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchValue, setSearchValue] = useState('');

  // Debounced search function
  const debouncedSearch = useCallback(
    (() => {
      let timeoutId: NodeJS.Timeout;
      return (searchTerm: string) => {
        clearTimeout(timeoutId);
        timeoutId = setTimeout(async () => {
          if (sourceId && accountId && searchTerm.length >= 2) {
            setLoading(true);
            try {
              const response = await adsRulesApi.fetchCampaignItems({
                type: 'ads',
                source_id: sourceId,
                account_id: accountId,
                campaign_ids: campaignIds?.map(id => parseInt(id)),
                group_ids: groupIds?.map(id => parseInt(id)),
                search: searchTerm
              });
              if (response.data.success) {
                setApiOptions(response.data.data.items);
              }
            } catch (error) {
              console.error('Error fetching ads:', error);
            } finally {
              setLoading(false);
            }
          }
        }, 300);
      };
    })(),
    [sourceId, accountId, campaignIds, groupIds]
  );

  // Load initial data
  useEffect(() => {
    if (sourceId && accountId && !searchValue) {
      setLoading(true);
      adsRulesApi.fetchCampaignItems({
        type: 'ads',
        source_id: sourceId,
        account_id: accountId,
        campaign_ids: campaignIds?.map(id => parseInt(id)),
        group_ids: groupIds?.map(id => parseInt(id))
      }).then(response => {
        if (response.data.success) {
          setApiOptions(response.data.data.items);
        }
      }).catch(error => {
        console.error('Error fetching ads:', error);
      }).finally(() => {
        setLoading(false);
      });
    }
  }, [sourceId, accountId, campaignIds, groupIds, searchValue]);

  // Handle search
  useEffect(() => {
    if (searchValue) {
      debouncedSearch(searchValue);
    }
  }, [searchValue, debouncedSearch]);

  // объединяем переданные options (из сохраненного правила) с данными API
  const displayOptions = React.useMemo(() => {
    const byId: Record<string, CampaignItem> = {} as any;
    (options || []).forEach((o) => { byId[o.id] = { id: o.id, name: o.name } as any; });
    apiOptions.forEach((o) => { byId[o.id] = o; });
    return Object.values(byId);
  }, [options, apiOptions]);
  const isLoading = loading || externalLoading;

  return (
    <Select
      mode="multiple"
      allowClear
      showSearch
      disabled={disabled}
      loading={isLoading}
      value={value}
      onChange={(vals) => onChange(Array.isArray(vals) ? vals : [])}
      onSearch={setSearchValue}
      placeholder={disabled ? 'Сначала выберите рекламный кабинет' : 'В работе все объявления, можете выбрать по названию или ID объявления'}
      style={{ width: '100%' }}
      dropdownStyle={{ background: '#fff' }}
      filterOption={false} // Disable client-side filtering since we use server-side search
      maxTagCount={10}
    >
      {displayOptions.map((option) => (
        <Select.Option key={option.id} value={option.id}>
          {`${option.name} (${option.id})`}
        </Select.Option>
      ))}
    </Select>
  );
}
